import { makeAutoObservable, runInAction } from 'mobx';
import {
  adminEmployeesCreate,
  adminEmployeesDelete,
  adminEmployeesGetList,
  adminEmployeesUpdate,
} from '@/services/employeesApi';
import { AppError } from '@/utils/errors';
import type {
  CreateEmployeePayload,
  EmployeeListItem,
  EmployeesListParams,
  UpdateEmployeePayload,
} from '@/types/employee.types';
import { toastStore } from './ToastStore';

const SEARCH_DEBOUNCE_MS = 400;
const DEFAULT_LIMIT = 20;

export interface EmployeesFilters {
  search: string;
  positionUuid: string | null;
  isActive: boolean | null;
  sort: EmployeesListParams['sort'];
  order: EmployeesListParams['order'];
}

const defaultFilters: EmployeesFilters = {
  search: '',
  positionUuid: null,
  isActive: null,
  sort: 'last_name',
  order: 'ASC',
};

/**
 * Список сотрудников сервисного центра: фильтры, пагинация, CRUD базовых полей.
 * Схема оплаты сотрудника — отдельный стор EmployeePaymentSchemeStore.
 */
class EmployeesStore {
  items: EmployeeListItem[] = [];
  total = 0;
  limit = DEFAULT_LIMIT;
  offset = 0;
  filters: EmployeesFilters = { ...defaultFilters };

  isLoading = false;
  isLoadingMore = false;
  isSaving = false;
  submitError: string | null = null;
  loadError: string | null = null;

  private serviceCenterUuid: string | null = null;
  private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private fetchRequestId = 0;

  constructor() {
    makeAutoObservable(this);
  }

  setServiceCenterUuid = (serviceCenterUuid: string) => {
    if (this.serviceCenterUuid === serviceCenterUuid) return;
    this.serviceCenterUuid = serviceCenterUuid;
    this.offset = 0;
    this.fetchList();
  };

  setSearch = (search: string) => {
    this.filters.search = search;
    this.offset = 0;

    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = setTimeout(() => {
      this.fetchList();
    }, SEARCH_DEBOUNCE_MS);
  };

  setPositionFilter = (positionUuid: string | null) => {
    this.filters.positionUuid = positionUuid;
    this.offset = 0;
    this.fetchList();
  };

  setActiveFilter = (isActive: boolean | null) => {
    this.filters.isActive = isActive;
    this.offset = 0;
    this.fetchList();
  };

  setSort = (sort: EmployeesFilters['sort'], order: EmployeesFilters['order']) => {
    this.filters.sort = sort;
    this.filters.order = order;
    this.fetchList();
  };

  setPage = (offset: number) => {
    this.fetchList(offset);
  };

  get hasMore(): boolean {
    return this.items.length < this.total;
  }

  private buildParams = (offset: number): EmployeesListParams => ({
    service_center_uuid: this.serviceCenterUuid as string,
    search: this.filters.search || undefined,
    position_uuid: this.filters.positionUuid ?? undefined,
    is_active: this.filters.isActive ?? undefined,
    sort: this.filters.sort,
    order: this.filters.order,
    limit: this.limit,
    offset,
  });

  fetchList = async (offset = 0) => {
    if (!this.serviceCenterUuid) return;

    const requestId = ++this.fetchRequestId;
    this.isLoading = true;
    // fetchList и loadMore делят один requestId — если поиск/фильтр меняются
    // во время подгрузки следующей страницы, устаревший loadMore выходит по
    // requestId и не сбрасывает свой флаг; сбрасываем его здесь, иначе
    // isLoadingMore зависает true навсегда и блокирует дальнейшую подгрузку.
    this.isLoadingMore = false;
    this.offset = offset;
    this.loadError = null;

    try {
      const response = await adminEmployeesGetList(this.buildParams(offset));

      if (requestId !== this.fetchRequestId) return;

      runInAction(() => {
        this.items = response.data ?? [];
        this.total = response.total ?? 0;
        this.isLoading = false;
      });
    } catch {
      if (requestId !== this.fetchRequestId) return;
      runInAction(() => {
        this.isLoading = false;
        this.loadError = 'Не удалось загрузить список сотрудников';
      });
      toastStore.showError('Не удалось загрузить список сотрудников');
    }
  };

  /**
   * Подгружает следующую порцию поверх уже загруженных (бесконечный скролл) —
   * в отличие от fetchList не сбрасывает items, а дописывает в конец.
   */
  loadMore = async () => {
    if (!this.serviceCenterUuid) return;
    if (this.isLoading || this.isLoadingMore) return;
    if (!this.hasMore) return;

    const nextOffset = this.items.length;
    const requestId = ++this.fetchRequestId;
    this.isLoadingMore = true;

    try {
      const response = await adminEmployeesGetList(this.buildParams(nextOffset));

      if (requestId !== this.fetchRequestId) {
        runInAction(() => {
          this.isLoadingMore = false;
        });
        return;
      }

      runInAction(() => {
        this.items = [...this.items, ...(response.data ?? [])];
        this.total = response.total ?? this.total;
        this.offset = nextOffset;
        this.isLoadingMore = false;
      });
    } catch {
      runInAction(() => {
        this.isLoadingMore = false;
      });
      if (requestId !== this.fetchRequestId) return;
      toastStore.showError('Не удалось подгрузить сотрудников');
    }
  };

  createEmployee = async (payload: CreateEmployeePayload): Promise<boolean> => {
    this.isSaving = true;
    this.submitError = null;
    try {
      await adminEmployeesCreate(payload);
      runInAction(() => {
        this.isSaving = false;
      });
      toastStore.showSuccess('Сотрудник добавлен');
      await this.fetchList();
      return true;
    } catch (error) {
      runInAction(() => {
        this.isSaving = false;
        this.submitError = this.resolveSubmitError(error);
      });
      return false;
    }
  };

  // Тост об успехе НЕ показываем здесь: на EmployeeEditPage следом ещё
  // уходит пакет изменений схемы оплаты, и «успех» должен относиться к
  // сохранению целиком, а не только к личным данным (см. handleSave).
  updateEmployee = async (uuid: string, payload: UpdateEmployeePayload): Promise<boolean> => {
    this.isSaving = true;
    this.submitError = null;
    try {
      await adminEmployeesUpdate(uuid, payload);
      runInAction(() => {
        this.isSaving = false;
      });
      await this.fetchList();
      return true;
    } catch (error) {
      runInAction(() => {
        this.isSaving = false;
        this.submitError = this.resolveSubmitError(error);
      });
      return false;
    }
  };

  clearSubmitError = () => {
    this.submitError = null;
  };

  deleteEmployee = async (uuid: string): Promise<boolean> => {
    try {
      await adminEmployeesDelete(uuid);
      toastStore.showSuccess('Сотрудник удалён');
      await this.fetchList();
      return true;
    } catch {
      toastStore.showError('Не удалось удалить сотрудника');
      return false;
    }
  };

  private resolveSubmitError = (error: unknown): string => {
    if (error instanceof AppError) {
      if (error.statusCode === 409) {
        return 'Сотрудник с таким телефоном уже есть на этой мойке';
      }
      // 400 — ошибка валидации конкретных полей (class-validator на бэке),
      // причина отличается от кейса к кейсу — показываем реальный текст
      // бэка, а не одну фразу на все случаи (иначе причину не отличить).
      return error.message || 'Проверьте корректность введённых данных';
    }
    return 'Не удалось сохранить. Попробуйте ещё раз.';
  };
}

export const employeesStore = new EmployeesStore();
export type { EmployeesStore };
